/**
 * MyVoiceCalls — Espace utilisateur des appels vocaux CONFILEX.
 *
 * - Liste des appels passés (statut, durée, consentement, synthèse)
 * - Détail avec synthèse éditable (chronologie, faits clés, urgence, pièces, etc.)
 * - Droit RGPD à l'effacement
 * - Validation de la synthèse avant transmission au professionnel
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, Clock, ShieldCheck, ShieldX, Trash2, Edit3, Check, X,
  AlertTriangle, FileText, Calendar, Mic, Volume2, Loader2, ChevronLeft,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Card } from "@/components/ui/card";
import { apiClient, formatApiError } from "@/lib/api";
import PremiumPhoneButton from "@/components/PremiumPhoneButton";

const URGENCY_COLORS = {
  faible: "bg-[#063D25]/60 text-emerald-700 border-emerald-200",
  modérée: "bg-[#F5C400]/10 text-[#F5C400] border-[#F5C400]/30",
  élevée: "bg-red-900/20 text-red-700 border-red-900/40",
};

const STATUS_LABELS = {
  initiated: "Initié",
  queued: "En file d'attente",
  ringing: "Sonnerie",
  "in-progress": "En cours",
  in_progress_intro: "Présentation",
  consent_given: "Consentement accordé",
  consent_denied: "Sans enregistrement",
  synthesizing: "Synthèse en cours",
  completed: "Terminé",
  mocked: "Simulé",
  error: "Erreur",
  failed: "Échec",
  busy: "Occupé",
  "no-answer": "Sans réponse",
};

export default function MyVoiceCalls() {
  const { callId } = useParams();
  const nav = useNavigate();
  const [calls, setCalls] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await apiClient.get("/voice/calls/me");
      setCalls(r.data.items || []);
    } catch (e) {
      toast.error("Impossible de charger vos appels.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!callId) { setDetail(null); return; }
    apiClient.get(`/voice/calls/${callId}`)
      .then(r => setDetail(r.data))
      .catch(e => toast.error(formatApiError(e?.response?.data?.detail) || "Détail indisponible."));
  }, [callId]);

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <Seo title="Mes appels vocaux · CONFILEX" />
      <Navbar variant="light" />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#F5C400] mb-2">Espace personnel</p>
            <h1 className="font-serif italic text-4xl text-[#031B12]">Mes appels vocaux</h1>
            <p className="text-sm text-[#BFC8C2] mt-2 max-w-2xl">
              Retrouvez ici l'historique de vos échanges avec l'assistant vocal CONFILEX et les synthèses générées.
              Vous pouvez les corriger, compléter ou supprimer à tout moment.
            </p>
          </div>
          <PremiumPhoneButton variant="pill" label="Nouvel appel" />
        </div>

        {detail ? (
          <CallDetail
            call={detail}
            onClose={() => nav("/mes-appels")}
            onUpdate={(d) => { setDetail(d); refresh(); }}
            onDelete={() => { refresh(); nav("/mes-appels"); }}
          />
        ) : (
          <CallsList calls={calls} loading={loading} />
        )}
      </div>
      <Footer />
    </div>
  );
}

// ============================================================================
function CallsList({ calls, loading }) {
  if (loading) {
    return <p className="text-[#BFC8C2]/85"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Chargement…</p>;
  }
  if (!calls.length) {
    return (
      <Card className="p-10 text-center rounded-2xl border-slate-100" data-testid="voice-calls-empty">
        <Phone className="w-10 h-10 mx-auto text-slate-300 mb-4" />
        <p className="text-[#BFC8C2] mb-2">Vous n'avez pas encore eu d'appel avec notre assistant.</p>
        <p className="text-xs text-[#BFC8C2]/85">Cliquez sur "Nouvel appel" pour démarrer votre première conversation.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="voice-calls-list">
      {calls.map(c => (
        <Link
          key={c.id}
          to={`/mes-appels/${c.id}`}
          data-testid={`voice-call-row-${c.id}`}
          className="block"
        >
          <Card className="p-5 rounded-2xl border-slate-100 hover:border-[#F5C400]/40 transition cursor-pointer">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-full bg-[#031B12]/5 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-[#031B12]" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-[#031B12] truncate">
                    {c.purpose === "expert_intro" ? "Présentation de spécialiste" :
                     c.purpose === "reminder" ? "Rappel" :
                     "Structuration de dossier"}
                  </p>
                  <p className="text-xs text-[#BFC8C2]/85">
                    {new Date(c.created_at).toLocaleString("fr-FR", {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {c.consent_recording === true && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 bg-[#063D25]/60 px-2 py-1 rounded">
                    <ShieldCheck className="w-3 h-3" /> Enregistré
                  </span>
                )}
                {c.consent_recording === false && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#BFC8C2] bg-[#031B12] px-2 py-1 rounded">
                    <ShieldX className="w-3 h-3" /> Sans enregistrement
                  </span>
                )}
                {c.duration_sec != null && (
                  <span className="text-xs text-[#BFC8C2]/85 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {Math.round(c.duration_sec / 60)} min
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-wider text-[#031B12] bg-[#F5C400]/15 px-2 py-1 rounded">
                  {STATUS_LABELS[c.status] || c.status}
                </span>
              </div>
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
}

// ============================================================================
function CallDetail({ call, onClose, onUpdate, onDelete }) {
  const synthesis = call.synthesis || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(synthesis);
  const [saving, setSaving] = useState(false);
  const [missions, setMissions] = useState([]);
  const [missionPicker, setMissionPicker] = useState(false);
  const [attaching, setAttaching] = useState(false);

  useEffect(() => { setForm(call.synthesis || {}); }, [call]);
  useEffect(() => {
    if (synthesis.validated_by_user && !call.attached_mission_id) {
      apiClient.get("/missions").then(r => setMissions(r.data || [])).catch(() => {});
    }
  }, [synthesis.validated_by_user, call.attached_mission_id]);

  const attachToMission = async (missionId) => {
    setAttaching(true);
    try {
      await apiClient.post(`/voice/calls/${call.id}/attach-to-mission`, { mission_id: missionId });
      toast.success("Synthèse transmise au dossier — le spécialiste pourra la consulter.");
      const r = await apiClient.get(`/voice/calls/${call.id}`);
      onUpdate(r.data);
      setMissionPicker(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Échec de transmission.");
    } finally {
      setAttaching(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/voice/calls/${call.id}/synthesis`, form);
      const r = await apiClient.get(`/voice/calls/${call.id}`);
      onUpdate(r.data);
      setEditing(false);
      toast.success("Synthèse enregistrée.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Échec d'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/voice/calls/${call.id}/synthesis`, {
        ...form, validated_by_user: true,
      });
      const r = await apiClient.get(`/voice/calls/${call.id}`);
      onUpdate(r.data);
      toast.success("Synthèse validée et prête à transmettre.");
    } catch {
      toast.error("Échec de validation.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cet appel et toutes les données associées ? Cette action est irréversible.")) {
      return;
    }
    try {
      await apiClient.delete(`/voice/calls/${call.id}`);
      toast.success("Appel supprimé.");
      onDelete();
    } catch {
      toast.error("Échec de la suppression.");
    }
  };

  const set = (k) => (v) => setForm(s => ({ ...s, [k]: v }));

  return (
    <div data-testid="voice-call-detail">
      <button
        onClick={onClose}
        className="text-xs text-[#BFC8C2]/85 hover:text-[#031B12] inline-flex items-center gap-1 mb-4"
        data-testid="voice-detail-back"
      >
        <ChevronLeft className="w-3 h-3" /> Retour à la liste
      </button>

      <Card className="p-6 rounded-2xl border-slate-100 mb-4">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#F5C400] mb-1">
              {STATUS_LABELS[call.status] || call.status}
            </p>
            <h2 className="font-serif italic text-2xl text-[#031B12]">
              {call.purpose === "expert_intro" ? "Présentation de spécialiste" :
               call.purpose === "reminder" ? "Rappel" :
               "Structuration de votre dossier"}
            </h2>
            <p className="text-xs text-[#BFC8C2]/85 mt-1">
              {new Date(call.created_at).toLocaleString("fr-FR", {
                weekday: "long", day: "2-digit", month: "long", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
              {call.duration_sec != null && ` · ${Math.round(call.duration_sec / 60)} min`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {synthesis.validated_by_user && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-[#063D25]/60 px-2 py-1 rounded-full">
                <Check className="w-3 h-3" /> Validée
              </span>
            )}
            <button
              data-testid="voice-detail-delete"
              onClick={remove}
              className="text-xs text-red-400 hover:text-red-700 inline-flex items-center gap-1"
              title="Droit à l'effacement RGPD"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          </div>
        </div>

        {call.consent_recording === false && (
          <div className="p-3 rounded-lg bg-[#031B12] border border-[#063D25] flex items-start gap-2 mb-4">
            <ShieldX className="w-4 h-4 text-[#BFC8C2]/85 mt-0.5 shrink-0" />
            <p className="text-xs text-[#BFC8C2]">
              Vous avez refusé l'enregistrement. Seules quelques notes synthétiques sont disponibles ci-dessous.
            </p>
          </div>
        )}

        {/* Synthèse */}
        <div className="space-y-5">
          <SynthesisField
            label="Identité" value={form.identity}
            onChange={set("identity")} editing={editing}
            testid="syn-identity"
          />
          <SynthesisField
            label="Rôle / Qualité" value={form.role}
            onChange={set("role")} editing={editing}
            testid="syn-role"
          />
          <SynthesisField
            label="Type de dossier" value={form.case_type}
            onChange={set("case_type")} editing={editing}
            testid="syn-case-type"
          />

          {/* Urgence */}
          <div>
            <p className="text-xs uppercase tracking-wider text-[#BFC8C2]/85 mb-2">Niveau d'urgence</p>
            {editing ? (
              <select
                data-testid="syn-urgency"
                value={form.urgency_level || "faible"}
                onChange={(e) => set("urgency_level")(e.target.value)}
                className="px-3 py-2 rounded-lg border border-[#063D25] text-sm"
              >
                <option value="faible">Faible</option>
                <option value="modérée">Modérée</option>
                <option value="élevée">Élevée</option>
              </select>
            ) : (
              <span className={`inline-block text-xs px-3 py-1 rounded-full border ${URGENCY_COLORS[form.urgency_level] || URGENCY_COLORS.faible}`}>
                {form.urgency_level || "faible"}
              </span>
            )}
          </div>

          {/* Chronologie */}
          <ListField
            label="Chronologie"
            items={form.chronology || []}
            renderItem={(item) => (
              <>
                {item.date && <span className="text-[10px] uppercase tracking-wider text-[#F5C400] mr-2">{item.date}</span>}
                {item.fact}
              </>
            )}
            editing={editing}
            onChange={set("chronology")}
            itemKey="fact"
            placeholderText="Date + fait (ex: 2025-03-12: Signature du contrat)"
            parseInput={(s) => {
              const m = s.match(/^([\d/-]+)\s*[:\-]\s*(.+)$/);
              return m ? { date: m[1], fact: m[2] } : { date: null, fact: s };
            }}
            testid="syn-chronology"
          />

          <ListField
            label="Faits importants"
            items={(form.key_facts || []).map(f => ({ text: f }))}
            renderItem={(it) => it.text}
            editing={editing}
            onChange={(arr) => set("key_facts")(arr.map(x => x.text))}
            itemKey="text"
            placeholderText="Un fait important"
            parseInput={(s) => ({ text: s })}
            testid="syn-key-facts"
          />

          <ListField
            label="Documents disponibles"
            items={(form.documents_available || []).map(f => ({ text: f }))}
            renderItem={(it) => it.text}
            editing={editing}
            onChange={(arr) => set("documents_available")(arr.map(x => x.text))}
            itemKey="text"
            placeholderText="Ex: Contrat, factures, mails…"
            parseInput={(s) => ({ text: s })}
            testid="syn-docs-available"
          />

          <ListField
            label="Documents manquants"
            items={(form.documents_missing || []).map(f => ({ text: f }))}
            renderItem={(it) => it.text}
            editing={editing}
            onChange={(arr) => set("documents_missing")(arr.map(x => x.text))}
            itemKey="text"
            placeholderText="À demander…"
            parseInput={(s) => ({ text: s })}
            testid="syn-docs-missing"
          />

          <ListField
            label="Actions suggérées"
            items={(form.suggested_actions || []).map(f => ({ text: f }))}
            renderItem={(it) => it.text}
            editing={editing}
            onChange={(arr) => set("suggested_actions")(arr.map(x => x.text))}
            itemKey="text"
            placeholderText="Étape à entreprendre"
            parseInput={(s) => ({ text: s })}
            testid="syn-actions"
          />

          <SynthesisField
            label="Professionnel suggéré"
            value={form.professional_type_suggested}
            onChange={set("professional_type_suggested")}
            editing={editing}
            testid="syn-pro-suggested"
          />

          <SynthesisField
            label="Synthèse en un paragraphe"
            value={form.summary_one_paragraph}
            onChange={set("summary_one_paragraph")}
            editing={editing}
            multiline
            testid="syn-summary"
          />
        </div>

        {/* Disclaimer */}
        <p className="text-[10px] text-[#BFC8C2]/70 italic mt-6 leading-relaxed">
          {synthesis.disclaimer || "Synthèse générée automatiquement à partir d'un échange vocal. À vérifier et corriger avant transmission."}
        </p>

        {/* Transmission à un dossier (visible si synthèse validée et non encore attachée) */}
        {synthesis.validated_by_user && !call.attached_mission_id && (
          <div className="mt-6 p-4 rounded-xl bg-[#F7F7F2] border border-[#F5C400]/30" data-testid="transmit-section">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#F5C400] mb-1">Transmission</p>
                <h4 className="font-serif italic text-[#031B12]">Transmettre cette synthèse à un dossier</h4>
                <p className="text-xs text-[#BFC8C2] mt-1 max-w-md">
                  Le spécialiste assigné au dossier pourra consulter votre synthèse préqualifiée avant le premier contact.
                </p>
              </div>
              {!missionPicker ? (
                <button
                  onClick={() => setMissionPicker(true)}
                  data-testid="transmit-open"
                  className="px-4 py-2 rounded-lg bg-[#031B12] text-white text-sm hover:brightness-110"
                >
                  Choisir un dossier
                </button>
              ) : null}
            </div>
            {missionPicker && (
              <div className="mt-3 space-y-2">
                {missions.length === 0 ? (
                  <p className="text-xs text-[#BFC8C2]/85 italic">Vous n'avez pas encore de dossier ouvert.</p>
                ) : (
                  missions.map(m => (
                    <button
                      key={m.id}
                      data-testid={`transmit-mission-${m.id}`}
                      onClick={() => attachToMission(m.id)}
                      disabled={attaching}
                      className="w-full text-left p-3 rounded-lg bg-[#031B12] border border-[#063D25] hover:border-[#F5C400]/40 transition disabled:opacity-50"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-[#031B12]">{m.title || "Dossier sans titre"}</p>
                          <p className="text-xs text-[#BFC8C2]/85">{m.category} · {new Date(m.created_at).toLocaleDateString("fr-FR")}</p>
                        </div>
                        {m.voice_synthesis_call_id && (
                          <span className="text-[10px] text-[#F5C400] bg-[#F5C400]/10 px-2 py-0.5 rounded">Déjà une synthèse</span>
                        )}
                      </div>
                    </button>
                  ))
                )}
                <button
                  onClick={() => setMissionPicker(false)}
                  className="text-xs text-[#BFC8C2]/85 hover:text-[#031B12] mt-2"
                >
                  Annuler
                </button>
              </div>
            )}
          </div>
        )}

        {call.attached_mission_id && (
          <div className="mt-6 p-3 rounded-lg bg-[#063D25]/60 border border-emerald-200 text-xs text-emerald-800 inline-flex items-center gap-2" data-testid="transmitted-badge">
            <Check className="w-3.5 h-3.5" />
            Synthèse transmise au dossier. Le spécialiste assigné pourra la consulter.
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3 justify-end">
          {!editing ? (
            <>
              <button
                onClick={() => setEditing(true)}
                data-testid="voice-detail-edit"
                className="px-4 py-2 rounded-lg border border-[#063D25] text-sm text-[#031B12] hover:bg-[#031B12] inline-flex items-center gap-2"
              >
                <Edit3 className="w-3.5 h-3.5" /> Corriger
              </button>
              {!synthesis.validated_by_user && (
                <button
                  onClick={validate}
                  data-testid="voice-detail-validate"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-[#F5C400] text-[#031B12] font-medium text-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Valider la synthèse
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={() => { setEditing(false); setForm(call.synthesis || {}); }}
                className="px-4 py-2 rounded-lg border border-[#063D25] text-sm text-[#BFC8C2] inline-flex items-center gap-2"
              >
                <X className="w-3.5 h-3.5" /> Annuler
              </button>
              <button
                onClick={save}
                data-testid="voice-detail-save"
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-[#031B12] text-white text-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Enregistrer
              </button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

// ============================================================================
function SynthesisField({ label, value, onChange, editing, multiline = false, testid }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-[#BFC8C2]/85 mb-1.5">{label}</p>
      {editing ? (
        multiline ? (
          <textarea
            data-testid={testid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-lg border border-[#063D25] text-sm focus:outline-none focus:border-[#F5C400]"
          />
        ) : (
          <input
            type="text"
            data-testid={testid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-[#063D25] text-sm focus:outline-none focus:border-[#F5C400]"
          />
        )
      ) : (
        <p className="text-sm text-[#031B12]" data-testid={`${testid}-value`}>{value || <span className="text-[#BFC8C2]/70 italic">— non renseigné —</span>}</p>
      )}
    </div>
  );
}

function ListField({ label, items, renderItem, editing, onChange, itemKey, placeholderText, parseInput, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...items, parseInput(draft.trim())]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-[#BFC8C2]/85 mb-2">{label}</p>
      {(!items || items.length === 0) && !editing && (
        <p className="text-sm text-[#BFC8C2]/70 italic">— aucun élément —</p>
      )}
      <ul className="space-y-1.5">
        {(items || []).map((it, i) => (
          <li key={`${testid}-${i}-${(it[itemKey] || "").substring(0, 10)}`} className="text-sm text-[#031B12] flex items-start justify-between gap-2 group">
            <span>• {renderItem(it)}</span>
            {editing && (
              <button onClick={() => remove(i)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition">
                <X className="w-3 h-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {editing && (
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder={placeholderText}
            className="flex-1 px-3 py-2 rounded-lg border border-[#063D25] text-xs focus:outline-none focus:border-[#F5C400]"
          />
          <button onClick={add} className="px-3 py-2 rounded-lg bg-[#031B12] text-[#031B12] text-xs hover:bg-slate-200">+ Ajouter</button>
        </div>
      )}
    </div>
  );
}
